import React, { useState } from 'react';
import { candidateService } from '../services/candidateService';
import EvidenceDialog from './signalsources/EvidenceDialog';

// The candidate drill-down.
//
// It explains WHY twice over, because the two answer different questions: the
// sentences say whether to call, the bars say which part of the relationship is
// carrying the score. Sentences come first — a recruiter reads them, and only
// then asks where the number came from.
//
// The bars are the four fit-v2.0 factors, and reachability sits apart from them
// because it is a MULTIPLIER: it scales the four and can only ever reduce the
// total, so drawing it as a fifth bar would imply it adds points.
//
// Each sentence that has records behind it opens into them. "21 touches, the
// last one 5 years ago" is a claim until a recruiter can see twenty-one dated
// rows, and a ranking built on unverifiable claims is what fit-v2.0 replaced.
// Which reasons are drillable comes from the API (`drillable` on each reason),
// not from a list kept here — one place to change when a code gains records.
//
// Reuses the Signal Sources EvidenceDialog: same wire shape, same honest
// "showing N of M". A second dialog would be a second thing to keep consistent.

const BUCKET_STYLE = {
  call_now: { label: 'Call now', chip: 'bg-emerald-100 text-emerald-800 ring-emerald-600/20', score: 'bg-emerald-100 text-emerald-800' },
  warm: { label: 'Warm', chip: 'bg-amber-100 text-amber-800 ring-amber-600/20', score: 'bg-amber-100 text-amber-800' },
  track: { label: 'Track', chip: 'bg-sky-100 text-sky-800 ring-sky-600/20', score: 'bg-sky-100 text-sky-800' },
  low: { label: 'Background', chip: 'bg-gray-100 text-gray-700 ring-gray-500/20', score: 'bg-gray-100 text-gray-700' },
};

function Section({ title, children }) {
  return (
    <div className="border-t border-gray-100 pt-5 mt-5">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</h3>
      {children}
    </div>
  );
}

// Column headings per reason. The API returns the generic three-column shape,
// so only the labels differ and they live here rather than on the wire.
const EVIDENCE_COLUMNS = {
  engagement_volume: { primary: 'Activity', secondary: 'Note', at: 'When' },
  cv_at_client: { primary: 'Client', secondary: 'Role', at: 'Started' },
  cv_at_more_clients: { primary: 'Client', secondary: 'Role', at: 'Started' },
  extra_client_companies: { primary: 'Company', secondary: 'Roles there', at: 'Most recent' },
  hm_known_at_ex_employer: { primary: 'Hiring manager', secondary: 'At', at: '' },
  colleague_at_client: { primary: 'Colleague', secondary: 'Role', at: '' },
  is_client_contact: { primary: 'Client', secondary: 'Role', at: 'Since' },
  whereabouts_verified: { primary: 'Verified title', secondary: 'How', at: 'When' },
  true_placement: { primary: 'Client', secondary: 'Role', at: 'Placed' },
  true_placement_more: { primary: 'Client', secondary: 'Role', at: 'Placed' },
  pipeline_stage_reached: { primary: 'Stage', secondary: 'Client', at: 'When' },
};

export default function CandidateDrawer({ candidate, isOpen, onClose }) {
  const [evidence, setEvidence] = useState(null);

  const showEvidence = async (reason) => {
    setEvidence({ reason, loading: true, data: null, error: '' });
    try {
      const data = await candidateService.getCandidateEvidence(candidate.person_id, reason.code);
      setEvidence({ reason, loading: false, data, error: '' });
    } catch (e) {
      setEvidence({ reason, loading: false, data: null, error: e.message || 'Could not load the detail' });
    }
  };

  if (!isOpen || !candidate) return null;

  const style = BUCKET_STYLE[candidate.bucket] || BUCKET_STYLE.low;
  const factors = candidate.factors || [];
  const employments = candidate.employments || [];
  const activity = candidate.activity || {};
  const emails = candidate.emails || [];
  const phones = candidate.phones || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="candidate-drawer">
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />

      <aside className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {candidate.full_name || 'Unknown candidate'}
            </h2>
            {candidate.local_name && (
              <p className="text-sm text-gray-500 mt-0.5">{candidate.local_name}</p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              {[candidate.current_title, candidate.current_company].filter(Boolean).join(' · ') ||
                'Role unknown'}
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-3">
            {typeof candidate.fit_score === 'number' && (
              <span
                data-testid="drawer-score"
                title="Fit score"
                className={`w-11 h-11 rounded-lg flex items-center justify-center text-base font-bold ${style.score}`}
              >
                {candidate.fit_score}
              </span>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-700 text-xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          <span
            data-testid="drawer-bucket"
            className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ring-1 ring-inset ${style.chip}`}
          >
            {style.label}
          </span>

          <Section title="Why they are here">
            {(candidate.reasons || []).length > 0 ? (
              <ul className="space-y-2" data-testid="drawer-reasons">
                {candidate.reasons.map((r, i) => (
                  <li key={r.code || i} className="flex gap-2 text-sm text-gray-700">
                    <span className="text-gray-300 select-none">•</span>
                    <span>
                      {r.text}
                      {r.drillable && (
                        // A real button, not an underline: checking the records
                        // is a first-class action, and a dotted underline reads
                        // as decoration a user has to discover by hovering.
                        <button
                          type="button"
                          data-testid="reason-evidence"
                          onClick={() => showEvidence(r)}
                          aria-label={`Show the records behind: ${r.text}`}
                          className="ml-2 inline-flex items-center gap-1 align-[1px] rounded-md border border-gray-200
                                     bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-600
                                     hover:border-[#00C6AE] hover:text-[#00695C] hover:bg-teal-50
                                     focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00C6AE]"
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="3" aria-hidden="true">
                            <circle cx="11" cy="11" r="7" />
                            <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
                          </svg>
                          Show
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No evidence recorded.</p>
            )}
          </Section>

          {factors.length > 0 && (
            <Section title="Where the score comes from">
              <div className="space-y-3" data-testid="drawer-factors">
                {factors.map((f) => {
                  const pct = f.max > 0 ? Math.round((f.points / f.max) * 100) : 0;
                  return (
                    <div key={f.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">{f.label || f.name}</span>
                        <span className="text-sm font-medium text-gray-900">
                          {f.points}/{f.max}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="bg-[#1B3A6B] h-2 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                <div className="pt-2 mt-1 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Reachability <span className="text-gray-400">(multiplier)</span>
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    ×{(candidate.reachability ?? 1).toFixed(2)}
                  </span>
                </div>
              </div>
            </Section>
          )}

          <Section title="Work history">
            {employments.length > 0 ? (
              <ul className="space-y-3" data-testid="drawer-employments">
                {employments.map((e, i) => (
                  <li key={i} className="text-sm">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-900">
                        {e.company || 'Unnamed employer'}
                      </span>
                      {e.is_client && (
                        <span className="shrink-0 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                          your client
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500">
                      {e.title || 'Role not recorded'}
                      {(e.start_date || e.end_date) && (
                        <>
                          {' '}
                          · {e.start_date || '?'} – {e.end_date || 'present'}
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No work history on file.</p>
            )}
          </Section>

          <Section title="Contact history">
            {activity.count > 0 ? (
              <p className="text-sm text-gray-700" data-testid="drawer-activity">
                {activity.count} recorded {activity.count === 1 ? 'touch' : 'touches'}
                {activity.first && activity.last && (
                  <>
                    {' '}
                    between {activity.first} and {activity.last}
                  </>
                )}
                .
              </p>
            ) : (
              <p className="text-sm text-gray-500">Nobody has logged contact with them.</p>
            )}
          </Section>

          <Section title="How to reach them">
            {emails.length === 0 && phones.length === 0 ? (
              <p className="text-sm text-gray-500">No contact details on file.</p>
            ) : (
              <div className="space-y-2 text-sm" data-testid="drawer-contacts">
                {emails.map((e) => (
                  <div key={e} className="flex gap-2">
                    <span className="text-gray-400 w-14 shrink-0">Email</span>
                    <a href={`mailto:${e}`} className="text-[#1B3A6B] hover:underline break-all">
                      {e}
                    </a>
                  </div>
                ))}
                {phones.map((p) => (
                  <div key={p} className="flex gap-2">
                    <span className="text-gray-400 w-14 shrink-0">Phone</span>
                    <a href={`tel:${p}`} className="text-[#1B3A6B] hover:underline">
                      {p}
                    </a>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {(candidate.city || candidate.country) && (
            <Section title="Location">
              <p className="text-sm text-gray-700">
                {[candidate.city, candidate.country].filter(Boolean).join(', ')}
              </p>
            </Section>
          )}
        </div>
      </aside>

      {evidence && (
        <EvidenceDialog
          title={evidence.reason.text}
          code={evidence.reason.code}
          columns={EVIDENCE_COLUMNS[evidence.reason.code]}
          evidence={evidence.data}
          loading={evidence.loading}
          error={evidence.error}
          onClose={() => setEvidence(null)}
        />
      )}
    </div>
  );
}
